import React, { useEffect, useState } from "react";
import WorkWithUs from "../components/sections/WorkWithUs";
import BookRideSection from "../components/sections/BookRideSection";
import axios from "axios";
import { Link } from "react-router-dom";
import BlogSkeleton from "../components/skeletons/BlogSkeleton";
import useMeta from "../hooks/useMeta";
import Meta from "../components/common/Meta";

const Blog = () => {
    const [blogs, setBlogs] = useState([]);
    const [popularBlogs, setPopularBlogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const loadBlogs = async (page = 1) => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_APP_DOMAIN}/api/customer/website_blogs?page=${page}`
            );

            const data = res.data.websiteBlogs;
            setBlogs(data.data || []);
            setCurrentPage(data.current_page || 1);
            setLastPage(data.last_page || 1);
            setPopularBlogs(res.data.popularBlogs || []);
        } catch (err) {
            console.error(err);
            setBlogs([]);
            setPopularBlogs([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageClick = (page) => {
        if (page < 1 || page > lastPage) return;
        loadBlogs(page);
    };

    useEffect(() => {
        loadBlogs(currentPage);
    }, []);

    const renderPagination = () => {
        const pages = [];

        // Previous
        pages.push(
            <button
                key="prev"
                disabled={currentPage <= 1}
                onClick={() => handlePageClick(currentPage - 1)}
                className="pg-btn"
            >
                ❮
            </button>
        );

        // Pages
        for (let i = 1; i <= lastPage; i++) {
            if (i === 1 || i === lastPage || (i >= currentPage - 1 && i <= currentPage + 1)) {
                pages.push(
                    <button
                        key={i}
                        onClick={() => handlePageClick(i)}
                        className={`pg-btn ${i === currentPage ? "active" : ""}`}
                    >
                        {i}
                    </button>
                );
            } else if (
                (i === currentPage - 2 && i > 1) ||
                (i === currentPage + 2 && i < lastPage)
            ) {
                pages.push(
                    <span key={`dots-${i}`} className="pg-dots">
                        ...
                    </span>
                );
            }
        }

        // Next
        pages.push(
            <button
                key="next"
                disabled={currentPage >= lastPage}
                onClick={() => handlePageClick(currentPage + 1)}
                className="pg-btn"
            >
                ❯
            </button>
        );

        return pages;
    };
    const meta = useMeta("blogs");
    // console.log("blogs Page Meta:", meta);
    return (
        <>
            {/* Meta tags for SEO */}
            {meta && (
                <Meta
                    title={meta.meta_title}
                    description={meta.meta_description}
                    keywords={meta.meta_keywords}
                />
            )}
            <section className="blog_wrap section_pb section_pt">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-12">
                            <div className="titles">
                                <h2>Blogs</h2>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* LEFT SIDE – BLOG LIST */}
                        <div className="col-md-8 col-sm-12 col-12">
                            <div className="row g-4">
                                {loading ? (
                                    <BlogSkeleton count={4} />
                                    // <div className="col-12 text-center">Loading...</div>
                                ) : blogs.length === 0 ? (
                                    <div className="col-12 text-center py-5">
                                        <h4>No blogs available right now.</h4>
                                    </div>
                                ) : (
                                    blogs.map((blog) => (
                                        <div key={blog.id} className="col-md-6 col-sm-6 col-12">
                                            <div className="blog_col">
                                                <Link to={`/blog/${blog.slug}`}>
                                                    <div className="blog_image">
                                                        <img
                                                            src={blog.image_url ?? "/assets/img/all-images/default_blog.png"}
                                                            alt={blog.title}
                                                        />
                                                    </div>
                                                    <div className="blog_text">
                                                        <h2>{blog.title}</h2>
                                                        <div
                                                            className="blog_description"
                                                            dangerouslySetInnerHTML={{ __html: (blog.description ?? "").substring(0, 80) + "..." }}
                                                        />

                                                    </div>
                                                </Link>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            {/* Pagination */}
                            {lastPage > 1 && (
                                <div id="pagination-container" className="pagination-wrapper mt-4">
                                    {renderPagination()}
                                </div>
                            )}
                        </div>

                        {/* RIGHT SIDE – MOST POPULAR */}
                        <div className="col-md-4 col-sm-12 col-12">
                            <div className="most_popular">
                                <h3>Most popular</h3>
                                {loading ? (
                                    <>
                                        {[1, 2].map((i) => (
                                            <div key={i} className="most_popularCol skeleton">
                                                <div className="popular_left">
                                                    <div className="skeleton-line"></div>
                                                </div>
                                                <div className="popular_right skeleton-box" style={{ height: 60, width: 60 }}></div>
                                            </div>
                                        ))}
                                    </>
                                ) : popularBlogs.length === 0 ? (
                                    <p>No popular blogs available right now.</p>
                                ) : (
                                    popularBlogs.map((blog) => (
                                        <a
                                            key={blog.id}
                                            href={`/blog/${blog.slug}`}
                                            className="most_popularCol"
                                        >
                                            <div className="popular_left">
                                                <p>{blog.title}</p>
                                            </div>
                                            <div className="popular_right">
                                                <img
                                                    src={blog.image_url ?? "/assets/img/all-images/default_blog.png"}
                                                    alt={blog.title}
                                                />
                                            </div>
                                        </a>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Work with us section */}
            <WorkWithUs />

            {/* Book ride section */}
            <BookRideSection />
        </>
    );
};

export default Blog;
